'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { Box } from '@ironhack/design-system2/components/box';
import { Button } from '@ironhack/design-system2/components/button';
import { Flex } from '@ironhack/design-system2/components/flex';
import { FormFeedback } from '@ironhack/design-system2/components/form-feedback';
import { omit } from 'ramda';
import { useEffect, useState } from 'react';
import { type StructuredTextGraphQlResponse } from 'react-datocms';
import { FormProvider, useForm } from 'react-hook-form';

import { useFormRequest, usePageContext } from '@/hooks';
import { CheckboxField } from '@/app/components/client/checkbox-field';
import { StructuredContent } from '@/app/components/client/structured-content';
import { TextField } from '@/app/components/client/text-field';
import { TurnstileFormToken } from '@/lib/turnstile/components';
import { type ValidationMessageSet } from '@/lib/utils/form-validations';
import { getNurturingSchema } from '@/lib/utils/form-validations/forms/nurturing-form';
import { type EmailInput } from '@/lib/utils/form-validations/schemas/email-schema';

export type NurturingFormType = {
  active: boolean;
  buttonText: string;
  emailLabel: string;
  emailPlaceholder: string;
  emailValidationMessages: ValidationMessageSet;
  errorMessage: string;
  legalText: StructuredTextGraphQlResponse;
  newsletterEnabled: boolean;
  newsletterLabel: string;
  salesforceSource: string;
  successMessage: string;
};

type Props = {
  color: string;
  data: NurturingFormType;
};

type FormData = {
  cToken: string;
  newsletter: boolean;
} & EmailInput;

export const NurturingForm = (props: Props) => {
  const { color, data } = props;
  const {
    buttonText,
    emailLabel,
    emailPlaceholder,
    emailValidationMessages,
    errorMessage,
    legalText,
    newsletterEnabled,
    newsletterLabel,
    salesforceSource,
    successMessage,
  } = data;

  const {
    params: { language },
  } = usePageContext();

  const formSchema = getNurturingSchema({
    emailValidationMessages,
    submitError: errorMessage,
  });
  const formMethods = useForm<FormData>({
    defaultValues: { newsletter: false },
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    resolver: zodResolver(formSchema),
  });
  const {
    formState: { errors, isDirty, isSubmitting },
    handleSubmit,
    reset,
    setValue,
    watch,
  } = formMethods;
  const [formFeedback, setFormFeedback] = useState<{
    status: 'error' | 'success';
    message: string;
  } | null>();
  const formRequest = useFormRequest();
  const setCaptchaToken = (token: string) => setValue('cToken', token);
  const cToken = watch('cToken');

  useEffect(() => {
    if (isDirty && formFeedback?.status === 'success') {
      setFormFeedback(null);
    }
  }, [formFeedback?.status, isDirty]);

  const onSubmit = async (formData: FormData): Promise<void> => {
    setFormFeedback(null);
    try {
      await formRequest({
        data: {
          ...omit(['newsletter'], formData),
          emailOptIn: newsletterEnabled ? formData.newsletter : true,
          language,
          source: salesforceSource,
        },
        formType: 'nurturing',
      });
      setFormFeedback({ status: 'success', message: successMessage });
      reset();
    } catch {
      setFormFeedback({ status: 'error', message: errorMessage });
    }
  };

  return (
    <Box maxW="65ch" mt={2}>
      <FormProvider {...formMethods}>
        <form
          id="blogBannerNurturingForm"
          noValidate
          onSubmit={handleSubmit(onSubmit)}
        >
          <Flex direction="column">
            <TextField
              error={errors.email?.message}
              isRequired
              label={emailLabel}
              name="email"
              placeholder={emailPlaceholder}
              type="email"
            />
            {!cToken && (
              <TurnstileFormToken
                language={language}
                onVerify={setCaptchaToken}
              />
            )}
            {newsletterEnabled && (
              <CheckboxField
                error={errors.newsletter?.message}
                isRequired
                label={newsletterLabel}
                matchCaptcha
                mt={2}
                name="newsletter"
                pr={0}
                variant="inverse"
              />
            )}
            <Button isLoading={isSubmitting} mt={3} type="submit">
              {buttonText}
            </Button>
            {formFeedback?.status && <FormFeedback mt={2} {...formFeedback} />}
          </Flex>
        </form>
      </FormProvider>
      <StructuredContent
        alignItems="center"
        color={color}
        data={legalText}
        ignoreLinkColor={false}
        mt={2}
        textAlign="center"
      />
    </Box>
  );
};
